import { Avatar, Toggle } from '@pa/ui'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { Provider } from '../model/types'
import { useSettingsDraft } from '../SettingsDraftProvider'
import { PfTags } from './PfTags'
import { focusClass, useFocusRow } from './useFocusRow'
import { ProviderDetailsModal } from './ProviderDetailsModal'

/**
 * C-08 Providers (UserFlows C-08): Show, the name with credentials, specialty and PF tags; Works at "All shown
 * locations, clinic hours" (D-73) or "No locations shown"; Visit types "X of Y" with "Mapped to {types}" or "All
 * open visit types". [New] (Q-2) Edit details: specialty, bio and avatar colour.
 */
export function ProvidersTab({
  focus,
}: {
  /** A row id from a C-05 arrow: scrolled to and flashed. */
  focus?: string | undefined
} = {}) {
  const { draft, update, clinicId } = useSettingsDraft()
  useFocusRow(focus)
  const [editing, setEditing] = useState<string | null>(null)
  const anyLocationShown = draft.locations.some((l) => l.visible)
  const enabled = draft.visitTypes.filter((v) => v.enabled)
  const patch = (id: string, p: Partial<Provider>) => {
    update((d) => ({ ...d, providers: d.providers.map((x) => (x.id === id ? { ...x, ...p } : x)) }))
  }
  const current = editing ? draft.providers.find((p) => p.id === editing) : undefined
  return (
    <div className="card">
      <div className="table-wrap">
        <table className="table rtable set-table prov-table">
          <thead>
            <tr>
              <th scope="col">Show</th>
              <th scope="col">Provider</th>
              <th scope="col">Works at</th>
              <th scope="col">Visit types</th>
            </tr>
          </thead>
          <tbody>
            {draft.providers.map((p) => {
              const types = enabled.filter((v) => v.providerIds === null || v.providerIds.includes(p.id))
              const restricted = enabled.filter((v) => v.providerIds?.includes(p.id))
              return (
                <tr
                  key={p.id}
                  data-row={p.id}
                  className={`${p.visible ? '' : 'off'} ${focusClass(p.id, focus)}`.trim()}
                >
                  <td className="c-toggle">
                    <Toggle
                      checked={p.visible}
                      onChange={(v) => {
                        patch(p.id, { visible: v })
                      }}
                      label={`Show ${p.name}`}
                    />
                  </td>
                  <td className="c-name">
                    <div className="cell-person">
                      <Avatar name={p.name} color={p.color} />
                      <div>
                        <b>
                          {p.name}
                          {p.credentials ? `, ${p.credentials}` : ''} <PfTags removed={p.removedInPf} isNew={p.isNew} />
                        </b>
                        <span>{p.specialty}</span>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          aria-label={`Edit details for ${p.name}`}
                          onClick={() => {
                            setEditing(p.id)
                          }}
                        >
                          Edit details
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="c-locs" data-label="Works at">
                    {anyLocationShown ? (
                      <span>All shown locations, clinic hours</span>
                    ) : (
                      <span className="muted">No locations shown</span>
                    )}
                  </td>
                  <td className="c-types" data-label="Visit types">
                    <b>
                      {types.length} of {enabled.length}
                    </b>
                    <span className="cell-sub">
                      {restricted.length
                        ? `Mapped to ${restricted.map((v) => v.name).join(', ')}`
                        : 'All open visit types'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="card-foot hint">
        At least one provider must be shown. To limit a visit type to certain providers, use{' '}
        <Link to="/clinics/$clinicId/settings/booking/visit-types" params={{ clinicId }} className="link-in-text">
          Visit types → Providers
        </Link>
        .
      </div>
      {current && (
        <ProviderDetailsModal
          provider={current}
          onClose={() => {
            setEditing(null)
          }}
          onDone={(details) => {
            patch(current.id, details)
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}
